import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  TagOutlined,
} from '@ant-design/icons';
import { Button, Space, Table, Typography } from 'antd';
import { ColumnsType } from 'antd/es/table';
import React, { useCallback, useEffect, useState } from 'react';

import {
  createQuestionModuleAsAdmin,
  deleteQuestionModuleAsAdmin,
  fetchQuestionModulesAsAdmin,
  updateQuestionModuleAsAdmin,
} from '@/apis';
import { QuestionModuleModal } from '@/components/Admin/Modal';
import PageHeader from '@/components/Admin/PageHeader';
import StateRenderer from '@/components/Admin/StateRenderer';
import TableContainer from '@/components/Admin/TableContainer';
import { MessageType } from '@/enums';
import { useAppFeedback } from '@/hooks';
import {
  AdminQuestionModule,
  CreateQuestionModuleAsAdminRequest,
  UpdateQuestionModuleAsAdminRequest,
} from '@/types/apis/admin';
import { getModuleLabel } from '@/utils/adminLabels';
import { formatDateTime } from '@/utils/dateFormatter';

const { Text } = Typography;

const AdminQuestionModulesPage = () => {
  const { showMessage, showConfirm } = useAppFeedback();
  const [modules, setModules] = useState<AdminQuestionModule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [editing, setEditing] = useState<AdminQuestionModule | null>(null);
  const [isAddingModule, setIsAddingModule] = useState(false);
  const [modalMode, setModalMode] = useState<'add' | 'edit'>('edit');
  const [deletingModule, setDeletingModule] = useState<string | null>(null);
  const pageSize = 10;

  const loadModules = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetchQuestionModulesAsAdmin(page, pageSize);
      setModules(response.data.items);
      setTotal(response.data.total);
    } catch (err) {
      const errorMessage = '載入問題模組失敗，請稍後再試';
      setError(errorMessage);
      showMessage(errorMessage, MessageType.Error);
      console.error('Error loading question modules:', err);
    } finally {
      setLoading(false);
    }
  }, [page, showMessage]);

  useEffect(() => {
    loadModules();
  }, [loadModules]);

  const handleSave = async (
    moduleData:
      | AdminQuestionModule
      | Omit<AdminQuestionModule, 'id' | 'createdAt' | 'updatedAt'>,
  ) => {
    try {
      if (modalMode === 'edit' && 'id' in moduleData) {
        const updateData: UpdateQuestionModuleAsAdminRequest = {
          name: moduleData.name,
        };

        await updateQuestionModuleAsAdmin(moduleData.id, updateData);
        await loadModules();
        showMessage('問題模組更新成功', MessageType.Success);
      } else {
        const createData: CreateQuestionModuleAsAdminRequest = {
          name: moduleData.name,
        };

        await createQuestionModuleAsAdmin(createData);
        await loadModules();
        showMessage('問題模組新增成功', MessageType.Success);
      }
    } catch (err) {
      const message =
        modalMode === 'edit'
          ? '問題模組更新失敗，請稍後再試'
          : '問題模組新增失敗，請稍後再試';
      showMessage(message, MessageType.Error);
      console.error(
        `Error ${modalMode === 'edit' ? 'updating' : 'creating'} question module:`,
        err,
      );
    }
  };

  const handleDeleteConfirm = (moduleId: string) => {
    showConfirm(
      '確認刪除',
      '確定要刪除這個問題模組嗎？此操作無法復原。',
      async () => {
        try {
          setDeletingModule(moduleId);
          await deleteQuestionModuleAsAdmin(moduleId);
          await loadModules();
          showMessage('問題模組刪除成功', MessageType.Success);
        } catch (err) {
          showMessage('問題模組刪除失敗，請稍後再試', MessageType.Error);
          console.error('Error deleting question module:', err);
        } finally {
          setDeletingModule(null);
        }
      },
      undefined,
      '確定刪除',
      '取消',
      'danger',
    );
  };

  const moduleColumns: ColumnsType<AdminQuestionModule> = [
    {
      title: '排序',
      dataIndex: 'order',
      key: 'order',
      width: 48,
      render: (order: number) => <Text>{order}</Text>,
    },
    {
      title: '模組名稱',
      dataIndex: 'name',
      key: 'name',
      render: (name: string) => (
        <Space>
          <TagOutlined />
          <Text strong>{getModuleLabel(name)}</Text>
          <Text type="secondary">{name}</Text>
        </Space>
      ),
    },
    {
      title: '建立時間',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 144,
      render: (createdAt: string) => (
        <Text type="secondary" style={{ fontSize: '12px' }}>
          {formatDateTime(new Date(createdAt))}
        </Text>
      ),
    },
    {
      title: '更新時間',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 144,
      render: (updatedAt: string) => (
        <Text type="secondary" style={{ fontSize: '12px' }}>
          {formatDateTime(new Date(updatedAt))}
        </Text>
      ),
    },
    {
      title: '操作',
      key: 'actions',
      width: 80,
      render: (_, module) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => {
              setModalMode('edit');
              setEditing(module);
            }}
            title="編輯問題模組"
            size="small"
          />
          <Button
            type="text"
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteConfirm(module.id)}
            loading={deletingModule === module.id}
            title="刪除問題模組"
            danger
            size="small"
          />
        </Space>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="問題模組管理"
        description="管理問卷問題的模組標籤，組織和歸類各種題目。"
      />

      <TableContainer
        title="問題模組列表"
        actions={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setModalMode('add');
              setIsAddingModule(true);
              setEditing(null);
            }}
          >
            新增模組
          </Button>
        }
      >
        <StateRenderer
          loading={loading}
          error={error}
          empty={modules.length === 0}
          emptyMessage="開始建立您的第一個問題模組"
          emptyIcon={<TagOutlined />}
          onRetry={loadModules}
        >
          <Table
            scroll={{ x: 'max-content' }}
            columns={moduleColumns}
            dataSource={modules}
            rowKey="id"
            pagination={{
              current: page,
              pageSize: pageSize,
              total: total,
              onChange: setPage,
              showSizeChanger: false,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `第 ${range[0]}-${range[1]} 筆，共 ${total} 筆`,
            }}
            size="small"
            loading={loading}
          />
        </StateRenderer>
      </TableContainer>

      <QuestionModuleModal
        isOpen={!!editing || isAddingModule}
        module={editing}
        mode={modalMode}
        onClose={() => {
          setEditing(null);
          setIsAddingModule(false);
        }}
        onSave={handleSave}
      />
    </>
  );
};

export default AdminQuestionModulesPage;
